import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import {
  DocumentationLegendEmpty,
  DocumentationLegendList,
  DocumentationPanel,
  DocumentationSection,
} from './index';

/**
 * The Documentation surface, shared by every page that has one (the document
 * Editor's Summary drawer and the data-analysis view side panel).
 *
 * Figma 36557-158083 / 36557-195099: a Work-performed block padded 16 top and
 * bottom, then a Legend block padded 12, with no rule between them — plus a 16px
 * SIDE inset the shared surface owns itself, so every page renders identically
 * without each wrapper having to supply (or forget) it.
 *
 * WHY THE EDITOR AND THE ROWS ARE SLOTS. Both pages put a collaborative Tiptap
 * editor in the first block and a list of legend entries in the second, but they
 * bind to different Yjs entities and render different row components. This layer
 * is `shared`, which under the FSD boundary rules may import only from `shared` —
 * it cannot reach the Tiptap editor or the collab hook, both of which live in the
 * legacy island. Taking them as slots is therefore both the architecturally legal
 * option and the honest one: what is genuinely common here is the LAYOUT, and
 * that is exactly what this owns.
 */

/** Work performed — Figma pads it 16px top and bottom, 16px each side. */
const WorkSection = styled(DocumentationSection)({ padding: '16px 16px' });

/** Legend — 12px top and bottom, same 16px sides. Blocks run together, no rule. */
const LegendSection = styled(DocumentationSection)({ padding: '12px 16px' });

export interface DocumentationTabViewProps {
  /** Caption over the first block (e.g. "Work performed"). */
  workLabel: ReactNode;
  /** The page's own collaborative editor for the work-performed body. */
  workEditor: ReactNode;

  /** Caption over the legend block (e.g. "Legend"). */
  legendLabel: ReactNode;
  /** Optional muted helper line under the legend caption. */
  legendDescription?: ReactNode;
  /** Optional ⓘ tooltip content beside the legend caption. */
  legendInfo?: ReactNode;

  /** The legend rows. Rendered only when `isEmpty` is false. */
  children: ReactNode;
  isEmpty: boolean;
  /**
   * The entries are still loading — draw `loadingLabel` rather than the empty state.
   * Every caller derives `isEmpty` from a list that reads `[]` before the answer
   * arrives, so without this the empty state doubles as the loading state and the
   * block flashes "no marks yet" on every view switch.
   */
  isLoading?: boolean;
  /** The loading node — a slot, since `shared` cannot reach the app's `Loading`. */
  loadingLabel?: ReactNode;
  /** Hide the legend block entirely (the Editor omits it for some documents). */
  showLegend?: boolean;
  /** Draw a hairline above the legend block. */
  legendDivider?: boolean;

  /** Empty-state glyph, heading and optional how-it-works hints. */
  emptyIcon?: ReactNode;
  emptyHeading: ReactNode;
  /** A sentence under the heading when the entries could not be listed (not "there are none"). */
  emptyNote?: ReactNode;
  emptyHints?: ReactNode[];

  'data-testid'?: string;
}

export function DocumentationTabView({
  workLabel,
  workEditor,
  legendLabel,
  legendDescription,
  legendInfo,
  children,
  isEmpty,
  isLoading = false,
  loadingLabel,
  showLegend = true,
  legendDivider,
  emptyIcon,
  emptyHeading,
  emptyNote,
  emptyHints,
  'data-testid': dataTestId,
}: DocumentationTabViewProps) {
  return (
    <DocumentationPanel padding={0} spacing={0} data-testid={dataTestId}>
      <WorkSection label={workLabel} grow>
        {workEditor}
      </WorkSection>

      {showLegend && (
        <LegendSection
          label={legendLabel}
          description={legendDescription}
          info={legendInfo}
          divider={legendDivider}
        >
          <DocumentationLegendList
            isEmpty={isEmpty}
            isLoading={isLoading}
            loadingLabel={loadingLabel}
            emptyLabel={
              <DocumentationLegendEmpty
                icon={emptyIcon}
                heading={emptyHeading}
                note={emptyNote}
                hints={emptyHints}
              />
            }
          >
            {children}
          </DocumentationLegendList>
        </LegendSection>
      )}
    </DocumentationPanel>
  );
}
